import { Badge, Card, cn } from '@up2stay/up2promo-ui';

import type { CompetitorStanding } from '@/core/components/reputation/competitors/competitor-standing';
import type { Dictionary } from '@/core/providers/dictionary-provider';

type Dict = Dictionary['reputation']['competitorsPage']['standing'];

function RankTile({
  label,
  rank,
  total,
  shared,
  warn,
  dict,
}: {
  label: string;
  rank: number;
  total: number;
  shared: boolean;
  warn: boolean;
  dict: Dict;
}) {
  return (
    <div
      className={cn(
        'min-w-0 flex-1 rounded-[var(--radius-nested)] px-3.5 py-3',
        warn ? 'bg-[color:var(--warn-bg)]' : 'bg-[color:var(--surface-2)]',
      )}
    >
      <div
        className={cn(
          'text-[11.5px]',
          warn ? 'text-[color:var(--warn-fg)]' : 'text-muted-foreground',
        )}
      >
        {label}
      </div>
      <div className="mt-0.5 text-lg font-bold">
        {rank === 0 ? (
          <>
            –{' '}
            <span className="text-muted-foreground text-xs font-medium">
              {dict.ratingUnknown}
            </span>
          </>
        ) : (
          <>
            {dict.rank.replace('{rank}', String(rank))}{' '}
            <span className="text-muted-foreground text-xs font-medium">
              {shared
                ? dict.shared
                : dict.rankOf.replace('{total}', String(total))}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/** "Where you stand": one-line verdict, rank tiles and the volume ranking. */
export function CompetitorStandingCard({
  standing,
  yourRating,
  dict,
  locale,
  className,
}: {
  standing: CompetitorStanding;
  yourRating?: number;
  dict: Dict;
  locale: string;
  className?: string;
}) {
  const max = Math.max(...standing.volumeRows.map((row) => row.count), 1);
  return (
    <Card className={cn('flex min-w-0 flex-col gap-3.5 p-5 sm:p-6', className)}>
      <p className="text-muted-foreground text-[12.5px]">{dict.eyebrow}</p>
      <p className="text-[22px] font-bold leading-[1.2] tracking-tight text-balance sm:text-[28px]">
        {dict.quality[standing.quality]}{' '}
        <span className="text-muted-foreground">
          {standing.quality === 'none'
            ? dict.volumeNoRating[standing.volume]
            : dict.volume[standing.volume]}
        </span>
      </p>
      <div className="flex gap-2.5">
        <RankTile
          label={dict.ratingLabel}
          rank={standing.ratingRank}
          total={standing.ratingTotal}
          shared={standing.ratingShared && standing.ratingRank === 1}
          warn={false}
          dict={dict}
        />
        <RankTile
          label={dict.volumeLabel}
          rank={standing.volumeRank}
          total={standing.volumeTotal}
          shared={false}
          warn={standing.volume === 'last'}
          dict={dict}
        />
      </div>

      <ol className="flex flex-col">
        {standing.volumeRows.map((row, index) => (
          <li
            key={row.id}
            className={cn(
              'grid grid-cols-[24px_minmax(0,1fr)_minmax(44px,auto)] items-center gap-2.5 border-b py-2.5 text-[13px] last:border-b-0 sm:grid-cols-[24px_minmax(0,1fr)_minmax(44px,auto)_110px]',
              row.isYou &&
                '-mx-2.5 rounded-[10px] border-b-0 bg-accent px-2.5 font-semibold',
            )}
          >
            <span
              className={cn(
                'font-mono',
                row.isYou
                  ? 'text-[color:var(--ghost-accent-fg)]'
                  : 'text-muted-foreground',
              )}
            >
              {index + 1}
            </span>
            <span className="flex min-w-0 items-center gap-1.5">
              {/* Two lines so the street of same-name branches stays
                  visible (DEV-822). */}
              <span
                className="line-clamp-2 [overflow-wrap:anywhere]"
                title={row.name}
              >
                {row.name}
              </span>
              {row.isYou ? (
                <Badge variant="info" className="px-1.5 py-0 text-[10.5px]">
                  {dict.you}
                </Badge>
              ) : null}
            </span>
            {/* One line even from 10.000 reviews on (DEV-822). */}
            <span className="whitespace-nowrap text-right font-mono tabular-nums">
              {row.count.toLocaleString(locale)}
            </span>
            <span className="hidden h-1.5 rounded-full bg-muted sm:block">
              <span
                className={cn(
                  'block h-full rounded-full',
                  row.isYou ? 'bg-primary' : 'bg-[color:var(--chart-3)]',
                )}
                style={{ width: `${Math.max(3, (row.count / max) * 100)}%` }}
              />
            </span>
          </li>
        ))}
      </ol>

      <p className="text-muted-foreground mt-auto text-xs">
        {standing.quality === 'none'
          ? dict.footerNoRating
          : standing.allTied && yourRating != null
            ? dict.footerTied
                .replace('{count}', String(standing.ratingTotal))
                .replace('{rating}', yourRating.toFixed(1))
            : dict.footerRank}
      </p>
    </Card>
  );
}
